'use client'

import React, { ReactElement, ReactNode, useId } from 'react'
import { TextTypes } from '../../../types'
import { Button, ButtonProps, Text, Tooltip } from '../../atoms'
import { SwitcherProps } from '../../molecules'
import { FilterActions } from '../Filter/FilterActions'
import { Filters, FiltersProps } from '../Filter/Filters'

export interface TableTopControlsProps {
    title?: ReactNode
    /** Size of the title. Has no default value so a size is implicitly chosen for the usecase. NOTE: "TextTypes.HEADING_1" renders an actual "h1" */
    titleHeadingSize?: TextTypes.HEADING_1 | TextTypes.HEADING_2 | TextTypes.HEADING_3
    description?: ReactNode
    /** Slot for a (filter/search) component(s) */
    leftContent?: ReactElement
    filterContentSwitcher?: ReactElement<SwitcherProps>
    filters?: ReactElement<FiltersProps, typeof Filters>
    actions?: ReactElement<ButtonProps, typeof Button> | Array<ReactElement<ButtonProps, typeof Button>>
}

export const TableTopControls = ({
    title,
    titleHeadingSize,
    description,
    leftContent,
    filterContentSwitcher,
    filters,
    actions,
}: TableTopControlsProps): ReactElement => {
    const showDescriptionAsTooltip = !!(leftContent || filters || filterContentSwitcher)
    const id = useId()

    return (
        <div className="flex flex-col w-full">
            <div className="flex justify-start items-center gap-6 py-4">
                {title && titleHeadingSize && (
                    <div className={`inline-flex ${!showDescriptionAsTooltip ? 'flex-col' : 'gap-2 items-center'}`}>
                        <Text type={titleHeadingSize}>{title}</Text>
                        {description && showDescriptionAsTooltip && (
                            <Tooltip id={`${id}-table-title`} content={description} place="bottom" />
                        )}
                        {description && !showDescriptionAsTooltip && (
                            <Text className="max-w-[36rem]">{description}</Text>
                        )}
                    </div>
                )}

                {leftContent && <div className="inline-flex shrink-0">{leftContent}</div>}

                {filterContentSwitcher && <div className="shrink-0 inline-flex">{filterContentSwitcher}</div>}

                {filters && <FilterActions />}

                {actions && <div className="inline-flex gap-2 ml-auto shrink-0">{actions}</div>}
            </div>

            {filters && <Filters>{filters}</Filters>}
        </div>
    )
}
